import {
  Autocomplete, TextField, Box, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/system';
import {
  theme,
  getReaderLocation,
  getReaderNames,
  getAvailableLanguages,
  getAvailableFocus,
} from '../../lib/index';
import { MultiselectFocus } from '../MultiselectFocus/index';
import { MultiselectLanguages } from '../MultiselectLanguages/index';
import { FilterTabs } from '../FilterTabs/index';

// create component
function Filter({
  data, filter, setFilter, resetFilter, screenWidth,
}) {
  const readerNames = data.length > 0 ? getReaderNames(data).sort() : [];
  const readerLocations = data.length > 0 ? getReaderLocation(data).sort() : [];
  const focalPoints = data.length > 0 ? getAvailableFocus(data) : [];
  const languages = data.length > 0 ? getAvailableLanguages(data) : [];

  // add selected values to filter-object and send to parent component
  const handleFilterChange = (event, value, reason) => {
    // handle keypress
    if (event.type === 'keydown') {
      const obj = { ...filter };
      if (event.key === 'Enter') {
        if (readerNames.includes(value)) {
          obj.name = value;
        }
        if (readerLocations.includes(value)) {
          obj.location = value;
        }
        setFilter(obj);
      }
    } else {
      const obj = { ...filter };
      if (reason === 'selectOption') {
        const key = event.target.parentNode.dataset.id;
        obj[key] = value;
      } else if (reason === 'reset' || reason === 'clear' || value === null) {
        const key = event.target.dataset.id !== undefined
          ? event.target.dataset.id
          : event.target.parentNode.dataset.id;
        obj[key] = null;
      }
      setFilter(obj);
    }
  };

  return (
    <StyledContainer className="filter-container">
      {
        screenWidth < 1025
          ? (
            <FilterTabs
              handleFilterChange={handleFilterChange}
              filter={filter}
              setFilter={setFilter}
              readerNames={readerNames}
              focalPoints={focalPoints}
              readerLocations={readerLocations}
              languages={languages}
              resetFilter={resetFilter}
            />
          )
          : (
            <>
              <StyledAutocomplete
                clearIcon={<ClearIcon fontSize="small" data-id="name" />}
                ListboxProps={{ 'data-id': 'name' }}
                componentsProps={{
                  'input#:r1:': {
                    'data-id': 'name',
                  },
                }}
                onChange={handleFilterChange}
                options={readerNames}
                popupIcon={<SearchIcon />}
                renderInput={(params) => <TextField {...params} label="nach Reader suchen" />}
                value={filter.name ? filter.name : null}
              />
              <MultiselectFocus
                focalPoints={focalPoints}
                filter={filter}
                setFilter={setFilter}
              />
              <Tooltip title="alle Filter zurücksetzen">
                <ClearAllFilters onClick={resetFilter} />
              </Tooltip>
              <StyledAutocomplete
                clearIcon={<ClearIcon fontSize="small" data-id="location" />}
                ListboxProps={{ 'data-id': 'location' }}
                onChange={handleFilterChange}
                options={readerLocations}
                popupIcon={<SearchIcon />}
                renderInput={(params) => <TextField {...params} label="Standorte" />}
                value={filter.location ? filter.location : null}
              />
              <MultiselectLanguages
                languages={languages}
                filter={filter}
                setFilter={setFilter}
              />
            </>
          )
          }
    </StyledContainer>
  );
}

export default Filter;

// styling
const StyledContainer = styled(Box, {})({
  position: 'relative',

  display: 'flex',
  marginBottom: 30,
  padding: '0 20px',

  gap: 20,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',

  [theme.breakpoints.up('laptop')]: {
    marginBottom: 60,
  },
});

const StyledAutocomplete = styled(Autocomplete, {})({
  width: 'calc((100% - 110px) / 4)',

  borderRadius: 0,

  '& .MuiAutocomplete-popupIndicatorOpen.MuiButtonBase-root': {
    transform: 'rotate(0deg)',
  },

  '&.Mui-focused': {
    border: 'none',
  },

  '& label.Mui-focused': {
    color: theme.palette.fontgrey,
  },

  '& .MuiOutlinedInput-root': {
    borderRadius: 0,

    '&.Mui-focused fieldset': {
      borderColor: theme.palette.newSecondary,
    },
  },

  '& .MuiAutocomplete-clearIndicator': {
    padding: 0,
  },
});

const ClearAllFilters = styled(DeleteIcon, {})({
  opacity: 0.8,

  color: theme.palette.placeholdergrey,
  fontSize: 30,

  transition: 'opacity 250ms ease-in-out',

  cursor: 'pointer',

  '&:hover': {
    opacity: 0.6,

    transition: 'opacity 250ms ease-in-out',
  },
});
